import React, { memo, useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Image from 'react-bootstrap/Image';
import Adresse from '../composants/Adresse';
import Don from '../composants/Don';
import EvenementsContainer from './EvenementsContainer';
import '../fichierCss/Accueil.css';
import RessourceApi from '../api/RessourceApi';
import image1 from '../images/galeries/image1.jpg';
import { obtenirImageDepuisRessource } from '../utils/ressourceImageUtils';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(2, 1),
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3, 1),
      marginTop: theme.spacing(2),
    },
  },
  heroCard: {
    borderRadius: 8,
    boxShadow: '0 14px 28px rgba(7, 33, 52, 0.12)',
    border: '1px solid rgba(15, 45, 63, 0.12)',
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fcff 100%)',
    padding: theme.spacing(1.2),
    height: '100%',
  },
  mediaWrap: {
    width: '100%',
    height: 420,
    borderRadius: 8,
    overflow: 'hidden',
    background: '#eef5fa',
    border: '1px solid rgba(11, 36, 65, 0.12)',
    [theme.breakpoints.down('md')]: {
      height: 300,
    },
  },
  media: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  badge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 6,
    background: '#e2f3ff',
    border: '1px solid #b8e1ff',
    color: '#0f3b53',
    fontWeight: 700,
    fontSize: '0.8rem',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  title: {
    margin: 0,
    color: '#102f43',
    fontWeight: 800,
    lineHeight: 1.2,
  },
  descriptionBox: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: '1px solid rgba(15, 45, 63, 0.1)',
    background: '#ffffff',
    maxHeight: 320,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  paragraph: {
    margin: 0,
    marginBottom: theme.spacing(1.2),
    lineHeight: 1.72,
    color: '#2a3e4d',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
  sideCard: {
    borderRadius: 8,
    boxShadow: '0 10px 20px rgba(7, 33, 52, 0.08)',
    border: '1px solid rgba(15, 45, 63, 0.1)',
    background: '#ffffff',
    padding: theme.spacing(1.6),
    height: '100%',
  },
  eventsCard: {
    display: 'flex',
    flexDirection: 'column',
  },
  sideTitle: {
    margin: 0,
    marginBottom: theme.spacing(1),
    color: '#12354a',
    fontWeight: 700,
  },
  eventsWrap: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    border: '1px solid rgba(15, 45, 63, 0.1)',
    borderRadius: 8,
    background: '#f8fcff',
    padding: theme.spacing(0.5),
  },
  actionRow: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-start',
  },
}));

const ContenuGeneriqueContainer = memo(() => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const contenuNavigation = location?.state?.contenu || null;
  const [contenu, setContenu] = useState(contenuNavigation);
  const [chargement, setChargement] = useState(false);

  useEffect(() => {
    if (contenuNavigation) {
      setContenu(contenuNavigation);
      return;
    }

    const id = searchParams.get('id');
    if (!id) {
      setContenu(null);
      return;
    }

    const charger = async () => {
      setChargement(true);
      try {
        const ressource = await RessourceApi.obtenirRessourceParId(id);
        if (!ressource) {
          setContenu(null);
          return;
        }

        setContenu({
          type: ressource.categorie || 'Ressource',
          id: ressource.id,
          titre: ressource.titre,
          description: ressource.description,
          image: obtenirImageDepuisRessource(ressource.cheminFichier, ressource.categorie, image1),
        });
      } catch (error) {
        setContenu(null);
      } finally {
        setChargement(false);
      }
    };

    charger();
  }, [contenuNavigation, searchParams]);

  const paragraphes = useMemo(() => {
    const texte = String(contenu?.description || '').trim();
    if (!texte) {
      return [];
    }

    return texte
      .split(/\n\s*\n/g)
      .map((p) => p.trim())
      .filter(Boolean);
  }, [contenu]);

  if (chargement) {
    return (
      <div className={classes.root}>
        <Paper className={classes.sideCard}>
          <h2 className={classes.title}>Chargement de la ressource...</h2>
        </Paper>
      </div>
    );
  }

  if (!contenu) {
    return (
      <div className={classes.root}>
        <Paper className={classes.sideCard}>
          <h2 className={classes.title}>Contenu introuvable</h2>
          <p className={classes.paragraph}>Cette page doit etre ouverte depuis une actualite ou un evenement.</p>
          <Button variant="contained" onClick={() => navigate('/actualites')}>Retour aux actualites</Button>
        </Paper>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12} md={8}>
          <Paper className={classes.heroCard}>
            <div className={classes.mediaWrap}>
              <Image src={contenu.image} alt={contenu.titre || 'Contenu'} className={classes.media} rounded={false} />
            </div>
            <div className={classes.badge}>{contenu.type}</div>
            <h1 className={classes.title}>{contenu.titre || 'Sans titre'}</h1>
            <div className={classes.descriptionBox}>
              {(paragraphes.length ? paragraphes : [contenu.description || '']).map((p, index) => (
                <p key={index} className={classes.paragraph}>{p}</p>
              ))}
            </div>
            <div className={classes.actionRow}>
              <Button variant="outlined" onClick={() => navigate(-1)}>Retour</Button>
            </div>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper className={`${classes.sideCard} ${classes.eventsCard}`}>
            <h3 className={classes.sideTitle}>Evenements a venir</h3>
            <Divider style={{ marginBottom: 12 }} />
            <div className={classes.eventsWrap}>
              <EvenementsContainer variant="list" />
            </div>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3} style={{ marginTop: 24 }}>
        <Grid item xs={12} md={8}>
          <Paper className={classes.sideCard} id="map">
            <h3 className={classes.sideTitle}>Adresse</h3>
            <Divider style={{ marginBottom: 12 }} />
            <Adresse />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper className={classes.sideCard}>
            <h3 className={classes.sideTitle}>Informations utiles</h3>
            <Divider style={{ marginBottom: 12 }} />
            <Don />
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
});

export default ContenuGeneriqueContainer;
